import {ID, ListRequest} from "../../../types/global.d";
import {PrimaryThicknessClass, ThicknessClass} from "./ThicknessClass.ts";
import {PrimaryTimberClass, TimberClass} from "./TimberClass.ts";
import {PrimaryTimberType, TimberType} from "./TimberType.ts";
import {PrimaryTimberStorage, TimberStorage} from "./TimberStorage.ts";
import {timberClassApi} from "../../apis/TimberClass.api.ts";
import {timberTypeApi} from "../../apis/TimberType.api.ts";
import {thicknessClassApi} from "../../apis/ThicknessClass.api.ts";
import {timberStorageApi} from "../../apis/TimberStorage.api.ts";
import {ShipmentState} from "./ShipmentState";
import {ShipmentType} from "./ShipmentType";
import {Cutting, PrimaryCutting} from "./Cutting.ts";
import {cuttingApi} from "../../apis/Cutting.api.ts";

export interface LogGroupQueryRequest {
    list: ListRequest,
    filter: {
        classId?: ID[],
        typeId?: ID[],
        buyerId?: ID,
        ownerId?: ID,
        cuttingId?: ID[],
        storageId?: ID[],
        shipmentId?: ID,
        shipmentDefined?: boolean,
        startCreate?: Date,
        endCreate?: Date,
        startShipment?: Date,
        endShipment?: Date,
    }
}

export interface LogGroupWithShipmentQueryResult {
    id: ID;
    key: string;
    reportKey: string;
    cuttingId: ID;
    storageId: ID;
    buyerId?: ID;
    shipmentAt?: Date;
    state: ShipmentState;
    shipmentType: ShipmentType;
    totalCount: number,
    totalVolume: number,
}

export interface LogGroupQueryResult {
    timberClassId: ID,
    timberTypeId: ID,
    thicknessClassId: ID,
    cuttingId: ID,
    storageId: ID,
    totalCount: number,
    totalVolume: number,
}

export interface TimberThicknessGroup {
    thicknessClass: ThicknessClass,
    count: number,
    sum: number,
}

export interface TimberClassGroup {
    timberClass: TimberClass,
    timberType: TimberType,
    items: Array<TimberThicknessGroup>,
    count: number,
    sum: number,
}

export interface TimberStorageGroup {
    storage: TimberStorage,
    items: Array<TimberClassGroup>,
    itemMap: Record<string, TimberClassGroup>,
    count: number,
    sum: number,
}

export interface TimberCuttingGroup {
    cutting: Cutting,
    items: Array<TimberStorageGroup>,
    itemMap: Record<string, TimberStorageGroup>,
    count: number,
    sum: number,
}

const createTimberThicknessGroup = async (dispatch, item: LogGroupQueryResult): Promise<TimberThicknessGroup> => {
    return {
        thicknessClass: (await dispatch(thicknessClassApi.endpoints.getThicknessClass.initiate(PrimaryThicknessClass.fromId(item.thicknessClassId))))?.data,
        count: item.totalCount,
        sum: item.totalVolume,
    }
}

const createTimberClassGroup = async (dispatch, item: LogGroupQueryResult): Promise<TimberClassGroup> => {
    return {
        timberClass: (await dispatch(timberClassApi.endpoints.getTimberClass.initiate(PrimaryTimberClass.fromId(item.timberClassId))))?.data,
        timberType: (await dispatch(timberTypeApi.endpoints.getTimberType.initiate(PrimaryTimberType.fromId(item.timberTypeId))))?.data,
        items: [await createTimberThicknessGroup(dispatch, item)],
        count: item.totalCount,
        sum: item.totalVolume,
    }
}

const createTimberStorageGroup = async (dispatch, item: LogGroupQueryResult): Promise<TimberStorageGroup> => {
    const group = await createTimberClassGroup(dispatch, item)
    const key = item.timberClassId + '_' + item.timberTypeId
    return {
        storage: (await dispatch(timberStorageApi.endpoints.getTimberStorage.initiate(PrimaryTimberStorage.fromId(item.storageId))))?.data,
        itemMap: {
            [key]: group,
        },
        items: [group],
        count: item.totalCount,
        sum: item.totalVolume,
    }
}

const createTimberCuttingGroup = async (dispatch, item: LogGroupQueryResult): Promise<TimberCuttingGroup> => {
    const group = await createTimberStorageGroup(dispatch, item)
    const key = item.storageId
    return {
        cutting: (await dispatch(cuttingApi.endpoints.getCutting.initiate(PrimaryCutting.fromId(item.cuttingId))))?.data,
        itemMap: {
            [key]: group
        },
        items: [group],
        count: item.totalCount,
        sum: item.totalVolume,
    }
}

export {
    createTimberThicknessGroup,
    createTimberClassGroup,
    createTimberStorageGroup,
    createTimberCuttingGroup,
}

export interface LogTotalQueryRequest {
    cuttingId: ID,
    storageId: ID,
    shipmentId: ID,
    shipmentDefined: boolean,
}

export interface LogTotalQueryResult {
    commited: boolean,
    totalCount: number,
    totalVolume: number,
}

export type TimelinePeriod = 'Day' | 'Month';

export interface TimelineReportRequest {
    period?: TimelinePeriod;
    classId?: ID[],
    typeId?: ID[],
    buyerId?: ID,
    ownerId?: ID,
    cuttingId?: ID[],
    storageId?: ID[],
    shipmentId?: ID,
    shipmentDefined?: boolean,
    startCreate?: Date,
    endCreate?: Date,
    startShipment?: Date,
    endShipment?: Date,
}

export interface TimelineReportResult {
    date: string;
    shipmentCount: number;
    totalCount: number;
    totalVolume: number;
}
